import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "@tanstack/react-router";
import { LogOut, Settings as SettingsIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

// Avatar initials from the Auth0 display name (first + last initial) or, when
// no name is set, the first two letters of the email local-part.
function initialsFrom(name: string | undefined, email: string | undefined): string {
  const source = (name ?? email ?? "").trim();
  if (source.length === 0) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0]! + parts[1][0]!).toUpperCase();
  }
  return (parts[0] ?? source).slice(0, 2).toUpperCase();
}

const AVATAR_CLASS =
  "grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary to-[#7d3a14] text-[11px] font-semibold text-on-primary";

// Top-right account avatar. Clicking it opens a popover with the user's
// name/email, a Settings link, and Logout. The inline name/email text and the
// standalone settings gear were removed from the bar in favour of this menu.
export function ToolbarProfile() {
  const { user, logout } = useAuth0();
  const name = typeof user?.name === "string" ? user.name : undefined;
  const email = typeof user?.email === "string" ? user.email : undefined;
  const displayName = name ?? email ?? "Account";

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((prev) => !prev)}
        className={AVATAR_CLASS}
      >
        {initialsFrom(name, email)}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Account"
          className="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-control border border-outline-variant bg-surface-container shadow-lg"
        >
          <div className="flex items-center gap-2.5 border-b border-outline-variant px-3 py-3">
            <span aria-hidden="true" className={AVATAR_CLASS}>
              {initialsFrom(name, email)}
            </span>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-sm font-semibold text-on-surface">
                {displayName}
              </span>
              {email !== undefined ? (
                <span className="truncate text-xs text-on-surface-variant">
                  {email}
                </span>
              ) : null}
            </div>
          </div>

          <div className="p-1">
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2.5 rounded-control px-3 py-2 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
            >
              <SettingsIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span>Settings</span>
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void logout({
                  logoutParams: { returnTo: window.location.origin },
                });
              }}
              className="flex w-full items-center gap-2.5 rounded-control px-3 py-2 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container-high"
            >
              <LogOut aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
